import { useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { RootState } from "../../app/store";
import { isFinishedTaskSlot, useSchedule } from "../../hooks/useSchedule";
import { ScheduleItemDto } from "../../features/calendar/calendar.types";
import { useNow } from "../../hooks/useNow";
import {
  NativeTaskPlayerState,
  sendToNative,
} from "../../services/nativeBridge";

/**
 * Mirrors the task player into the app's system notification:
 * - working: task title, clock counting up (or down to the Pomodoro end),
 *   task progress (time worked / planned effort)
 * - break: "Break", clock counting down the break (or up without Pomodoro)
 * - nothing running: how many of today's planned tasks are done
 * With a Pomodoro, the app also rings when the interval is over.
 * Sends only on change. Render once, inside the app only.
 */
function NativeTaskPlayerSync() {
  const { t } = useTranslation("components");

  const activeTask = useSelector((s: RootState) => s.tasks.activeTask);
  const { recordingActive, recorderStartTime, pomodoroStartTime } =
    useSelector((s: RootState) => s.tasks.recorder);
  const recordingType = useSelector(
    (s: RootState) => s.tasks.recorder.activeRecording?.type,
  );
  const workMinutes = useSelector(
    (s: RootState) => s.settings.settings?.workPomodoroDuration,
  );
  const breakMinutes = useSelector(
    (s: RootState) => s.settings.settings?.breakPomodoroDuration,
  );
  const { tasks, finishedTaskIds } = useSelector((s: RootState) => s.tasks);
  const { getTaskScheduleForDay, tasksScheduledToday } = useSchedule();

  // Recount at midnight
  const today = new Date(useNow()).toDateString();

  // Today's planned tasks, counted per task (a task can have several slots).
  // Same rules as the progress bar in TaskListFilterable: finishing a task
  // leaves its slot "OPEN", so finished tasks count as done; slots of deleted
  // tasks are left out.
  const todayCount = useMemo(() => {
    const activeTaskIds = new Set(tasks.map((task) => task.id));
    const isFinished = (item: ScheduleItemDto) =>
      isFinishedTaskSlot(item, finishedTaskIds);
    const slots = getTaskScheduleForDay(new Date()).filter(
      (item) =>
        item.status !== "DISCARDED" &&
        ((!!item.noteId && activeTaskIds.has(item.noteId)) ||
          isFinished(item)),
    );
    // Archived slots have no noteId anymore - the archive entry is the task
    const taskKey = (item: ScheduleItemDto) =>
      (item.archivedNoteId ?? item.noteId)!;
    const all = new Set(slots.map(taskKey));
    const done = new Set(
      slots
        .filter((item) => item.status === "FINISHED" || isFinished(item))
        .map(taskKey),
    );
    return { done: done.size, total: all.size };
  }, [tasks, finishedTaskIds, getTaskScheduleForDay, today]);

  const nextTaskTitle = useMemo(
    () => tasksScheduledToday(new Date(), true)[0]?.title,
    [tasksScheduledToday, today],
  );

  const state = useMemo((): NativeTaskPlayerState | null => {
    if (recordingActive && recorderStartTime) {
      const intervalStart = pomodoroStartTime ?? recorderStartTime;

      if (recordingType === "PAUSE") {
        return {
          mode: "break",
          title: t("NativeTaskPlayer.break"),
          subtitle: breakMinutes
            ? t("NativeTaskPlayer.breakPomodoro", { minutes: breakMinutes })
            : undefined,
          startedAt: recorderStartTime,
          endsAt: breakMinutes
            ? intervalStart + breakMinutes * 60000
            : undefined,
          progress: breakMinutes
            ? (Date.now() - intervalStart) / (breakMinutes * 60000)
            : undefined,
          progressPerMinute: breakMinutes ? 1 / breakMinutes : undefined,
          alert: breakMinutes
            ? {
                title: t("NativeTaskPlayer.breakOver"),
                body: t("NativeTaskPlayer.breakOverBody"),
              }
            : undefined,
        };
      }

      if (activeTask) {
        // timeEffort and recorded time are in seconds
        const effort = activeTask.timeEffort;
        const pastSeconds = (activeTask.timerRecords || []).reduce(
          (sum, record) =>
            record.endedAt
              ? sum +
                (new Date(record.endedAt).getTime() -
                  new Date(record.startedAt).getTime()) /
                  1000
              : sum,
          0,
        );
        const recordedSeconds =
          pastSeconds + (Date.now() - recorderStartTime) / 1000;

        return {
          mode: "work",
          title: activeTask.title || t("NativeTaskPlayer.untitled"),
          subtitle: workMinutes
            ? t("NativeTaskPlayer.workPomodoro", { minutes: workMinutes })
            : undefined,
          startedAt: recorderStartTime,
          endsAt: workMinutes ? intervalStart + workMinutes * 60000 : undefined,
          progress: effort ? recordedSeconds / effort : undefined,
          progressPerMinute: effort ? 60 / effort : undefined,
          alert: workMinutes
            ? {
                title: t("NativeTaskPlayer.workOver"),
                body: activeTask.title || undefined,
              }
            : undefined,
        };
      }
    }

    // Nothing running: nothing to report without a plan for today
    if (todayCount.total === 0) return null;
    return {
      mode: "idle",
      title: t("NativeTaskPlayer.todayDone", todayCount),
      subtitle:
        nextTaskTitle && todayCount.done < todayCount.total
          ? t("NativeTaskPlayer.next", { title: nextTaskTitle })
          : undefined,
      progress: todayCount.done / todayCount.total,
    };
    // Progress is computed at send time; the app keeps it growing from there
  }, [
    recordingActive,
    recorderStartTime,
    pomodoroStartTime,
    recordingType,
    activeTask,
    workMinutes,
    breakMinutes,
    todayCount,
    nextTaskTitle,
    t,
  ]);

  const lastSent = useRef<string | undefined>(undefined);
  useEffect(() => {
    // Compare without progress: it changes with every render-time Date.now()
    const key = JSON.stringify({ ...state, progress: undefined });
    if (key === lastSent.current) return;
    lastSent.current = key;
    sendToNative({ type: "TASK_PLAYER_STATE", state });
  }, [state]);

  return null;
}

export default NativeTaskPlayerSync;
